import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { ConsularCountryLanding } from "@/content/consular-corpus/countries";

function faqsTh(c: ConsularCountryLanding) {
  return [
    { q: `รับรองเอกสารเพื่อใช้ใน${c.countryTh} มีขั้นตอนอะไรบ้าง?`, a: c.chainTh.join(" → ") },
    { q: `${c.countryTh}เป็นภาคีอนุสัญญา Apostille หรือไม่?`, a: c.hague ? `เป็นภาคีอนุสัญญา Apostille (HCCH 1961) แต่ปัจจุบันไทยยังไม่เข้าเป็นภาคีจนถึง 28 กุมภาพันธ์ 2570 จึงยังต้องใช้เส้นทางนิติกรณ์กรมการกงสุลร่วมกับการรับรองของหน่วยงานผู้แทน${c.countryTh}ในไทย` : `ไม่ได้เป็นภาคีอนุสัญญา Apostille จึงต้องใช้เส้นทางนิติกรณ์กรมการกงสุลและการรับรองของหน่วยงานผู้แทน${c.countryTh}ในไทยตลอดไป` },
    { q: "หลังไทยเข้าเป็นภาคีจะเปลี่ยนอะไร?", a: c.afterApostilleTh },
    { q: "สถานทูตหรือหน่วยงานปลายทางกำหนดอะไรเป็นพิเศษ?", a: c.embassyNoteTh },
    { q: "ใช้ยื่นเรื่องอะไรได้บ้าง?", a: c.useCaseTh },
    { q: "สาเหตุที่เอกสารถูกตีกลับบ่อย?", a: c.pitfallsTh.join(" · ") },
    { q: "ใช้เวลาและค่าธรรมเนียมเท่าไหร่?", a: c.turnaroundTh },
    {
      q: "ต้องแปลก่อนหรือรับรองก่อน?",
      a: "ตามหลักปฏิบัติของกองสัญชาติและนิติกรณ์ ให้ขอเอกสารฉบับคัดรับรองจากหน่วยงานผู้ออกก่อน แล้วจึงแปล และนำคำแปลพร้อมต้นฉบับมายื่นนิติกรณ์ การแปลจากสำเนาที่ยังไม่คัดรับรองใหม่เป็นสาเหตุที่ต้องทำงานซ้ำบ่อยที่สุด",
    },
    {
      q: "มอบอำนาจให้ทีมงานยื่นแทนได้ไหม?",
      a: "ได้ครับ ใช้หนังสือมอบอำนาจติดอากรแสตมป์ 10 บาท พร้อมสำเนาบัตรประชาชนของผู้มอบและผู้รับมอบที่ลงนามรับรองสำเนาถูกต้อง หากผู้มอบอำนาจอยู่ต่างประเทศ ให้สถานเอกอัครราชทูตหรือสถานกงสุลไทยรับรองลายมือชื่อก่อน",
    },
  ];
}

export const Route = createFileRoute("/consular-legalization/country/$country")({
  loader: async ({ params }: { params: { country: string } }) => {
    const { CONSULAR_COUNTRY_LANDINGS, getConsularCountryLanding } = await import("@/content/consular-corpus/countries");
    const c = getConsularCountryLanding(params.country);
    if (!c) throw notFound();
    return { c, siblings: CONSULAR_COUNTRY_LANDINGS.filter((x) => x.slug !== c.slug).map((x) => ({ slug: x.slug, label: x.countryTh })) };
  },
  component: ConsularCountryPageTH,
  head: ({ params, loaderData }: { params: { country: string }; loaderData?: { c: ConsularCountryLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเทศปลายทาง" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { c } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `รับรองเอกสารใช้ใน${c.countryTh} 2026 — นิติกรณ์กงสุล + สถานทูต`,
        description: `เส้นทางรับรองเอกสารไทยเพื่อใช้ใน${c.countryTh}: ลำดับขั้นนิติกรณ์กรมการกงสุล ข้อกำหนดผู้แปล ค่าธรรมเนียม 200/400 บาท และผลของ Apostille ที่มีผลกับไทย 28 ก.พ. 2570`,
        path: `/consular-legalization/country/${params.country}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/consular-legalization/country/${params.country}` }],
        keywords: [
          `รับรองเอกสาร ${c.countryTh}`,
          `นิติกรณ์ ${c.countryTh}`,
          "นิติกรณ์กรมการกงสุล",
          "รับรองเอกสารสถานทูต",
          "Apostille ไทย 2570",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "รับรองเอกสารกงสุล", url: `${site}/consular-legalization` },
              { name: c.countryTh, url: `${site}/consular-legalization/country/${params.country}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsTh(c))) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `รับรองเอกสารไทยเพื่อใช้ใน${c.countryTh}`,
            step: c.chainTh.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/consular-legalization/country/${params.country}`)) },
      ],
    };
  },
});

function ConsularCountryPageTH() {
  const { c, siblings } = Route.useLoaderData();
  const faqs = faqsTh(c);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/consular-legalization/faq" className="hover:underline">รับรองกงสุล</a> <span>/</span>{" "}
          <span>{c.countryTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">รับรองเอกสารเพื่อใช้ใน{c.countryTh} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          สถานะอนุสัญญา Apostille ของ{c.countryTh}: <strong>{c.hague ? "เป็นภาคี" : "ไม่ได้เป็นภาคี"}</strong> ·
          อนุสัญญามีผลกับไทย 28 กุมภาพันธ์ 2570
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/consular-legalization/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">FAQ รับรองกงสุล</a>
          <a href={`/en/consular-legalization/country/${c.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">ลำดับขั้นการรับรอง (ก่อน 28 ก.พ. 2570)</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            {c.chainTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลาและค่าธรรมเนียม:</strong> {c.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ข้อกำหนดเฉพาะของหน่วยงานปลายทาง</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.embassyNoteTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใช้ยื่นเรื่องอะไรได้บ้าง</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.useCaseTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">สาเหตุที่เอกสารถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">เมื่ออนุสัญญา Apostille มีผลกับไทย</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.afterApostilleTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — เอกสารสำหรับ{c.countryTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ประเทศปลายทางอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((x: { slug: string; label: string }) => (
              <a key={x.slug} href={`/consular-legalization/country/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.label}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          แหล่งอ้างอิง: {c.sources.join(" · ")} — ข้อมูล ณ สิงหาคม 2569 (2026)
          ขั้นตอนและค่าธรรมเนียมนิติกรณ์อ้างอิงกองสัญชาติและนิติกรณ์ กรมการกงสุล ·
          สถานะอนุสัญญา Apostille อ้างอิง hcch.net (มีผลกับไทย 28 กุมภาพันธ์ 2570)
          โปรดตรวจสอบข้อกำหนดล่าสุดกับสถานทูตและหน่วยงานผู้รับก่อนดำเนินการ
        </p>
        <div className="mb-10">
          <PageGallery category="consular" seed="consular-country.country" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}
